import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Tag} from '@/components/ui/tag'
import {PorQue} from '@/components/ui/por-que'
import {HelpIcon} from '@/components/help-icon'
import {Skeleton} from '@/components/ui/skeleton'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {
  useAlcanceDePeligros,
  useConfirmarPeligros,
  usePeligrosDeLaObra,
  type AlcancePeligros,
} from '@/hooks/empresa/obras/use-obras'
import {translateApiError} from '@/lib/errors'

/** Sin sedes, sin filas y con filas tienen cada uno su frase: una obra sin peligros nunca se lee
 * como «cumple». Confirmar es acto de firmante y pasa siempre por el alcance (D13). */
export function ObraPeligros({
  businessId,
  proyectoId,
  canEdit,
  puedeFirmar,
  razonBloqueo,
}: {
  businessId: string
  proyectoId: string
  canEdit: boolean
  /** `capabilities.sgsst_firmar`, no `useGodModeReadOnly()`: confirmar es `require_firmante`. */
  puedeFirmar: boolean
  razonBloqueo: string | null
}) {
  const {t} = useTranslation()
  const {data, isLoading} = usePeligrosDeLaObra(businessId, proyectoId)
  const [marcados, setMarcados] = useState<string[] | null>(null)
  const [alcance, setAlcance] = useState<AlcancePeligros | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pedirAlcance = useAlcanceDePeligros(businessId, proyectoId)
  const confirmar = useConfirmarPeligros(businessId, proyectoId)

  // `marcados` es null hasta el primer clic para no pisar lo confirmado con una lista vacía.
  // Sin nada confirmado, la partida es la propuesta y la persona desmarca lo que no aplica.
  const confirmados = useMemo(
    () => (data?.propuestos ?? []).filter((p) => p.confirmado).map((p) => p.riesgo_fila_id),
    [data]
  )
  const propuestos = useMemo(() => (data?.propuestos ?? []).map((p) => p.riesgo_fila_id), [data])
  const partida = confirmados.length ? confirmados : propuestos
  const seleccion = marcados ?? partida

  function alternar(filaId: string, marcada: boolean) {
    setMarcados(marcada ? [...seleccion, filaId] : seleccion.filter((otra) => otra !== filaId))
  }

  function revisar() {
    setError(null)
    pedirAlcance.mutate(seleccion, {
      onSuccess: setAlcance,
      onError: (err) => setError(translateApiError(err)),
    })
  }

  if (isLoading || !data) return <Skeleton className="h-48 w-full" />

  return (
    <div className="space-y-4" data-testid="obra-peligros">
      <div className="flex flex-wrap items-center gap-1.5">
        <p className="font-heading text-base text-foreground">{t('obra.peligros.title')}</p>
        <HelpIcon label={t('common.help.iconLabelFor', {campo: t('obra.peligros.title')})} testId="obra-peligros-ayuda">
          {t('obra.peligros.porque')}
        </HelpIcon>
      </div>

      {data.sin_sedes && (
        <div
          className="space-y-1 rounded-lg bg-secondary/60 px-4 py-3 text-sm text-foreground"
          data-testid="obra-sin-sedes">
          <p>{t('obra.peligros.sinSedes')}</p>
          <PorQue label={t('common.porQue')} testId="obra-sin-sedes-porque">
            <p>{t('obra.peligros.sinSedesPorque')}</p>
          </PorQue>
        </div>
      )}
      {!data.sin_sedes && data.sin_areas && (
        <div
          className="space-y-1 rounded-lg bg-secondary/60 px-4 py-3 text-sm text-foreground"
          data-testid="obra-sin-areas">
          <p>{t('obra.peligros.sinAreas')}</p>
          <PorQue label={t('common.porQue')} testId="obra-sin-areas-porque">
            <p>{t('obra.peligros.sinAreasPorque')}</p>
          </PorQue>
        </div>
      )}
      {!data.sin_sedes && !data.sin_areas && data.propuestos.length === 0 && (
        <p className="rounded-lg bg-secondary/60 px-4 py-3 text-sm text-foreground" data-testid="obra-sin-propuestos">
          {t('obra.peligros.sinPropuestos')}
        </p>
      )}
      {data.confirmados.length === 0 && (
        <div
          className="space-y-1 rounded-lg bg-secondary/60 px-4 py-3 text-sm text-foreground"
          data-testid="obra-sin-peligros-confirmados">
          <p>{t('obra.peligros.sinConfirmados')}</p>
          <PorQue label={t('common.porQue')} testId="obra-sin-peligros-confirmados-porque">
            <p>{t('obra.peligros.sinConfirmadosPorque')}</p>
          </PorQue>
        </div>
      )}

      {canEdit && puedeFirmar && data.propuestos.length > 0 && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setMarcados(propuestos)} data-testid="peligros-marcar-todos">
            {t('obra.peligros.marcarTodos')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMarcados([])} data-testid="peligros-desmarcar-todos">
            {t('obra.peligros.desmarcarTodos')}
          </Button>
        </div>
      )}

      <ul className="space-y-2">
        {data.propuestos.map((peligro) => (
          <li
            key={peligro.riesgo_fila_id}
            className="surface flex items-start gap-3 px-4 py-3"
            data-testid={`peligro-${peligro.riesgo_fila_id}`}>
            <Checkbox
              aria-label={peligro.descripcion}
              checked={seleccion.includes(peligro.riesgo_fila_id)}
              disabled={!canEdit || !puedeFirmar}
              onCheckedChange={(marcada) => alternar(peligro.riesgo_fila_id, marcada === true)}
              data-testid={`peligro-check-${peligro.riesgo_fila_id}`}
            />
            <div className="min-w-0 space-y-1">
              <p className="text-sm text-foreground">{peligro.descripcion}</p>
              <p className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">
                {t('obra.peligros.fila', {numero: peligro.numero_fila})}
                {' · '}
                {peligro.nivel ? t('obra.peligros.nivel', {nivel: peligro.nivel}) : t('obra.peligros.sinNivel')}
                {peligro.area_nombre ? ` · ${t('obra.peligros.area', {area: peligro.area_nombre})}` : ''}
              </p>
            </div>
            {peligro.confirmado && (
              <Tag tone="green" className="ml-auto shrink-0">
                {t('obra.peligros.confirmados')}
              </Tag>
            )}
          </li>
        ))}
      </ul>

      {data.confirmados.length > 0 && (
        <ul className="space-y-1" data-testid="obra-peligros-firma">
          {data.confirmados.map((peligro) => (
            <li key={peligro.id} className="text-xs text-muted-foreground">
              {t('obra.peligros.fila', {numero: peligro.numero_fila})} —{' '}
              {peligro.confirmado_por_nombre
                ? t('obra.peligros.confirmadoPor', {
                    nombre: peligro.confirmado_por_nombre,
                    fecha: (peligro.confirmado_en ?? '').slice(0, 10),
                  })
                : t('obra.peligros.confirmados')}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      {canEdit && !data.sin_sedes && puedeFirmar && (
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={revisar} disabled={pedirAlcance.isPending} data-testid="peligros-revisar">
            {t('obra.peligros.guardar')}
          </Button>
          <PorQue label={t('obra.peligros.soloFirmanteLabel')} testId="peligros-solo-firmante">
            <p>{t('obra.peligros.soloFirmante')}</p>
          </PorQue>
        </div>
      )}
      {canEdit && !data.sin_sedes && !puedeFirmar && razonBloqueo && (
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="peligros-sin-firma">
          {razonBloqueo}
        </p>
      )}

      <AlcanceDialog
        alcance={alcance}
        pending={confirmar.isPending}
        onClose={() => setAlcance(null)}
        onConfirm={() => {
          if (!alcance) return
          confirmar.mutate(
            {riesgoFilaIds: seleccion, alcanceToken: alcance.alcance_token},
            {
              onSuccess: () => {
                setAlcance(null)
                setMarcados(null)
              },
              onError: (err) => {
                setAlcance(null)
                setError(translateApiError(err))
              },
            }
          )
        }}
      />
    </div>
  )
}

/** El alcance con nombres: «Juan, Ana y 21 más» se puede verificar, «23» no. */
function AlcanceDialog({
  alcance,
  pending,
  onClose,
  onConfirm,
}: {
  alcance: AlcancePeligros | null
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const {t} = useTranslation()
  return (
    <Dialog open={!!alcance} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="obra-alcance-dialog">
        <DialogHeader>
          <DialogTitle>{t('obra.alcance.title')}</DialogTitle>
        </DialogHeader>
        {alcance && (
          <div className="space-y-3 text-sm">
            <p data-testid="alcance-personas">
              {alcance.total_personas === 0
                ? t('obra.alcance.sinPersonas')
                : t('obra.alcance.personas', {count: alcance.total_personas})}
            </p>
            {/* Solo los tres primeros (R6) y sin enlace: navegar desde un diálogo de
                confirmación lo desmonta y pierde la confirmación pendiente. */}
            {alcance.personas.length > 0 && (
              <p className="text-muted-foreground" data-testid="alcance-primeros">
                {alcance.personas
                  .slice(0, 3)
                  .map((persona) => persona.nombre)
                  .join(' · ')}
                {alcance.total_personas > 3 && ` · ${t('obra.alcance.yMas', {count: alcance.total_personas - 3})}`}
              </p>
            )}
            <p data-testid="alcance-chequeos">
              {alcance.chequeos_destrabados === 0
                ? t('obra.alcance.sinChequeos')
                : t('obra.alcance.chequeos', {count: alcance.chequeos_destrabados})}
            </p>
            {/* La cifra de arriba solo cuenta chequeos que pasan a un color; sumar un
                peligro igual cambia lo que se le exige a toda la gente asignada. */}
            {alcance.peligros_entran > 0 && alcance.chequeos_destrabados === 0 && alcance.total_personas > 0 && (
              <p data-testid="alcance-se-recorren">
                {t('obra.alcance.seVuelvenACorrer', {count: alcance.total_personas})}
              </p>
            )}
            <p className="font-mono text-[11px] tracking-wide text-muted-foreground uppercase">
              {t('obra.alcance.entran', {count: alcance.peligros_entran})} ·{' '}
              {t('obra.alcance.salen', {count: alcance.peligros_salen})}
            </p>
            {alcance.se_queda_sin_peligros && (
              <p className="rounded-lg bg-secondary/60 px-3 py-2" data-testid="alcance-sin-peligros">
                {t('obra.alcance.seQuedaSinPeligros')}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={pending} onClick={onConfirm} data-testid="alcance-confirmar">
            {t('obra.alcance.confirmar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
